import { TimeseriesTableData, TimeseriesTableMetaData } from "./PanelDef";
import React from "react";
import { PanelViewComponent } from "../PanelDef";
import {
  PaginationProps,
  Table,
  TableHeaderCell,
  Dimmer,
  Popup,
  Icon,
  MenuItem,
} from "semantic-ui-react";
import { formatUtcDateTime, formatValue, TableContainer } from "../util";
import {
  fetchPanelData,
  fetchPanelDataFixedTimeRange,
  rowsPerPageOptions,
} from "../../../../../BytebeamClient";
import LoadingAnimation from "../../../../common/Loader";
import styled from "styled-components";
import { validateWholeNumber } from "../../../../../util";
import { beamtoast } from "../../../../common/CustomToast";
import {
  StyledCardSearchPageInput,
  StyledPagination,
  StyledSecondaryDevicePerPageWidget,
} from "../../../../common/commonStyledComps";
import { SelectDevicesPerPage } from "../../../DeviceManagement/Devices/Devices";
import { capitalizeFirstLetter } from "../../../util";

export const TimeRangeContainer = styled.div`
  height: fit-content;
  width: fit-content;
  margin: 4px 0px 4px 16px;
  padding: 10px 12px;
  background: ${({ theme }) =>
    theme.colors["time-range_container-background"]} !important;
  color: ${({ theme }) => theme.colors["time-range_container-color"]};
  border-radius: 4px;
  border: ${({ theme }) =>
    theme.colors["time-range_container-border"]} !important;
  box-shadow: ${({ theme }) =>
    theme.colors["time-range_container-box_shadow"]} !important;
`;

type ViewTimeseriesTableState = {
  data?: TimeseriesTableData;
  sortOrder?: string;
  currentPage: number;
  loading: boolean;
  currentStartTimestamp?: number;
  currentEndTimestamp?: number;
  inputPageNumber: number;
  devicesPerPage: number;
  totalPages: number;
  scrollLeft: number;
  viewportWidth: number;
};

// Past this many columns only the ones scrolled into view are rendered, with a
// spacer cell each side holding the scroll width.
const VIRTUALIZE_COLUMN_THRESHOLD = 40;
const COLUMN_OVERSCAN = 3;
const MIN_COLUMN_WIDTH = 90;
const MAX_COLUMN_WIDTH = 420;
// Semantic's cell padding plus border, added around the measured text.
const COLUMN_TEXT_PADDING = 30;
const FALLBACK_CELL_FONT = "400 13px Lato, 'Helvetica Neue', Arial, sans-serif";

type ColumnLayout = {
  sourceColumns?: string[];
  rows?: any[];
  font: string;
  columns: string[];
  /** Measured width of each data column. */
  widths: number[];
  /** offsets[i] is where column i starts; the last entry is the total width. */
  offsets: number[];
  /** Device id and timestamp, ahead of the windowed columns. */
  leadingWidths: number[];
};

/** Index of the last column starting at or before x. */
function findColumnAt(offsets: number[], x: number): number {
  let low = 0;
  let high = offsets.length - 2;
  while (low < high) {
    const mid = Math.ceil((low + high) / 2);
    if (offsets[mid] <= x) low = mid;
    else high = mid - 1;
  }
  return Math.max(0, low);
}

export class ViewTimeseriesTable extends PanelViewComponent<
  TimeseriesTableMetaData,
  TimeseriesTableData,
  ViewTimeseriesTableState
> {
  state = {} as ViewTimeseriesTableState;

  constructor(props) {
    super(props);

    this.state = {
      currentPage: 1,
      devicesPerPage: 20,
      inputPageNumber: 0,
      totalPages: 10,
      loading: false,
      scrollLeft: 0,
      viewportWidth: 0,
    };
  }

  scrollRef = React.createRef<HTMLDivElement>();
  scrollFrame: number | null = null;
  measureContext: CanvasRenderingContext2D | null = null;
  columnLayout: ColumnLayout | null = null;

  /** The cells' own font, so measurements match what paints. */
  getCellFont(): string {
    const cell = this.scrollRef.current?.querySelector("tbody td");
    if (!cell) return FALLBACK_CELL_FONT;
    const style = window.getComputedStyle(cell);
    return (
      style.font ||
      `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`.trim()
    );
  }

  measureText(text: string, font: string): number {
    if (!this.measureContext) {
      this.measureContext = document.createElement("canvas").getContext("2d");
    }
    if (!this.measureContext) return text.length * 8;
    if (this.measureContext.font !== font) this.measureContext.font = font;
    return this.measureContext.measureText(text).width;
  }

  /**
   * Width of each column is the widest of its header and its values on the
   * current page, measured rather than laid out so the offsets are known before
   * anything mounts. Recomputed only when columns, rows, or font change.
   */
  getColumnLayout(
    sourceColumns: string[] | undefined,
    rows: any[] | undefined,
    font: string
  ): ColumnLayout {
    const cached = this.columnLayout;
    if (
      cached &&
      cached.sourceColumns === sourceColumns &&
      cached.rows === rows &&
      cached.font === font
    ) {
      return cached;
    }

    const columns = (sourceColumns ?? []).filter(
      (column) => column !== "timestamp"
    );
    const pageRows = rows ?? [];

    const widths = columns.map((column) =>
      Math.min(
        MAX_COLUMN_WIDTH,
        Math.max(
          MIN_COLUMN_WIDTH,
          Math.ceil(this.measureText(column, font)) + COLUMN_TEXT_PADDING
        )
      )
    );

    pageRows.forEach((row) => {
      columns.forEach((column, index) => {
        if (widths[index] === MAX_COLUMN_WIDTH) return;
        const text = String(this.renderValue(column, row[column]) ?? "");
        const width = Math.min(
          MAX_COLUMN_WIDTH,
          Math.ceil(this.measureText(text, font)) + COLUMN_TEXT_PADDING
        );
        if (width > widths[index]) widths[index] = width;
      });
    });

    const offsets = new Array(columns.length + 1);
    offsets[0] = 0;
    for (let i = 0; i < columns.length; i++) {
      offsets[i + 1] = offsets[i] + widths[i];
    }

    // Reduced, not spread into Math.max: a large rows-per-page would otherwise
    // pass thousands of arguments in one call.
    const measureLeading = (header: string, values: string[]) => {
      const widest = values.reduce(
        (max, value) => Math.max(max, this.measureText(value, font)),
        this.measureText(header, font)
      );
      return Math.min(
        MAX_COLUMN_WIDTH,
        Math.max(MIN_COLUMN_WIDTH, Math.ceil(widest) + COLUMN_TEXT_PADDING)
      );
    };

    const serialKey = pageRows[0]?.["-serial_metadata"]
      ? Object.keys(pageRows[0]["-serial_metadata"])[0]
      : undefined;
    const leadingWidths = [
      measureLeading(
        serialKey ? `#${capitalizeFirstLetter(serialKey)}` : "Device ID",
        pageRows.map((row) =>
          String(
            serialKey ? row["-serial_metadata"]?.[serialKey] ?? "" : row.id
          )
        )
      ),
      // Room for the sort arrow semantic adds to this header.
      measureLeading(
        "Timestamp",
        pageRows.map((row) => formatUtcDateTime(row.timestamp))
      ) + 16,
    ];

    this.columnLayout = {
      sourceColumns,
      rows,
      font,
      columns,
      widths,
      offsets,
      leadingWidths,
    };
    return this.columnLayout;
  }

  // Coalesced into one frame: scroll fires far more often than the window changes.
  syncHorizontalWindow = () => {
    if (this.scrollFrame !== null) return;
    this.scrollFrame = window.requestAnimationFrame(() => {
      this.scrollFrame = null;
      const element = this.scrollRef.current;
      if (!element) return;
      const { scrollLeft, clientWidth } = element;
      if (
        scrollLeft !== this.state.scrollLeft ||
        clientWidth !== this.state.viewportWidth
      ) {
        this.setState({ scrollLeft, viewportWidth: clientWidth });
      }
    });
  };

  handlePaginationInputChange = (event) => {
    const newValue = event.target.value;
    this.setState({
      inputPageNumber: Number(newValue),
    });
  };

  handlePaginationInputKeyDown = (event, totalPages) => {
    if (event.key === "Enter" || event.keyCode === 13) {
      // If the pressed key is "Enter", trigger the function for changing active page
      if (validateWholeNumber(this.state.inputPageNumber.toString())) {
        this.handlePaginationChange(event, {
          activePage:
            this.state.inputPageNumber && this.state.inputPageNumber > 0
              ? this.state.inputPageNumber > totalPages
                ? (totalPages as number)
                : this.state.inputPageNumber
              : 1,
          totalPages: totalPages,
        });
        this.setState({
          inputPageNumber: 0,
        });
      } else {
        beamtoast.error("Please enter whole number for jump to page.");
      }
    }
  };

  async handlePaginationChange(
    event: React.MouseEvent<HTMLAnchorElement>,
    data: PaginationProps
  ) {
    if (data.activePage) {
      const sortOrder = this.state.sortOrder || this.props.panelMeta.sortOrder;
      this.refreshDataStateTime(
        data.activePage as number,
        sortOrder,
        this.state.devicesPerPage ?? this.props.panelMeta.rowsPerPage
      );
    }
  }

  changeDevicesPerPage(e, data) {
    try {
      this.setState({
        devicesPerPage: parseInt(data.value),
        currentPage: 1,
      });
      const sortOrder = this.state.sortOrder || this.props.panelMeta.sortOrder;
      this.refreshDataStateTime(1, sortOrder, data.value);
    } catch (e) {
      beamtoast.error("Failed to change number of devices per page");
      console.error("Error in changeDeviceStatus: ", e);
    }
  }

  async refreshDataStateTime(
    pageNumber: number,
    sortOrder: string,
    devicesPerPage: number
  ) {
    try {
      this.setState({ loading: true });
      const panelMeta: TimeseriesTableMetaData = Object.assign(
        {},
        this.props.panelMeta,
        {
          rowsPerPage: devicesPerPage,
          page: pageNumber,
          sortOrder: sortOrder,
        }
      );
      const response = await fetchPanelDataFixedTimeRange(
        this.props.dashboardId,
        panelMeta.id,
        [panelMeta],
        this.props.fetchParams,
        this.state.currentStartTimestamp,
        this.state.currentEndTimestamp
      );
      this.setState({
        data: response[0].data,
        currentPage: pageNumber,
        sortOrder: sortOrder,
        totalPages: Math.ceil(response[0].data.totalRows / devicesPerPage),
        loading: false,
      });
    } catch (error) {
      // Removing beamtoast here, so that no red beamtoast on network issues.
      console.error(
        "Error occurred in viewDashboard viewTimeSeriesTable refreshData. ",
        error
      );
    }
  }

  async refreshData(
    pageNumber: number,
    sortOrder: string,
    devicesPerPage: number
  ) {
    try {
      this.setState({ loading: true });
      const panelMeta = Object.assign({}, this.props.panelMeta, {
        rowsPerPage: devicesPerPage,
        page: pageNumber,
        sortOrder: sortOrder,
      });
      this.setState({
        currentStartTimestamp: this.props.fetchParams.timeRange
          .getStartTime()
          .toDate()
          .valueOf(),
        currentEndTimestamp: this.props.fetchParams.timeRange
          .getEndTime()
          .toDate()
          .valueOf(),
      });
      await fetchPanelData(
        this.props.dashboardId,
        panelMeta.id,
        [panelMeta],
        this.props.fetchParams,
        null,
        null
      );
      // Setting the state as undefined here, as the component will re-render and instead of reading this.state.data, it will read this.props.data
      this.setState({
        data: undefined,
        currentPage: 1,
        sortOrder: sortOrder,
        loading: false,
      });
    } catch (error) {
      // Removing beamtoast here, so that no red beamtoast on network issues.
      console.error(
        "Error occurred in viewDashboard viewTimeSeriesTable refreshData. ",
        error
      );
    }
  }

  onChangeSortOrder() {
    const sortOrder = this.state.sortOrder || this.props.panelMeta.sortOrder;
    const newSortOrder = sortOrder === "asc" ? "desc" : "asc";
    this.refreshDataStateTime(
      this.state.currentPage,
      newSortOrder,
      this.state.devicesPerPage
    );
  }

  onRefresh() {
    const currentPage = 1;
    this.refreshData(
      currentPage,
      this.state.sortOrder ?? this.props.panelMeta.sortOrder,
      this.state.devicesPerPage ?? this.props.panelMeta.rowsPerPage
    );
  }

  renderValue(column, value) {
    // Early return if the value is a sequence
    if (column === "sequence") {
      return value;
    }
    const enableUnits = this.props.panelMeta.enableUnits;
    const autoScaleUnits = this.props.panelMeta.autoScaleUnits;
    const enableRoundoff = this.props.panelMeta.enableRoundoff;
    const roundOffPrecision = this.props.panelMeta.roundOffPrecision;
    // Retrieve the column type
    const columnMeta = this.props.tables[this.props.panelMeta.table]?.find(
      (item) => item?.name === column
    );
    const columnType = columnMeta?.type;

    return formatValue(
      value,
      columnMeta?.unit,
      this.props.panelMeta.showNumericValuesInHex,
      enableRoundoff,
      roundOffPrecision,
      enableUnits,
      autoScaleUnits,
      columnType,
      column
    );
  }

  componentDidMount(): void {
    const data = this.state.data || this.props.data;
    this.setState({
      currentStartTimestamp: this.props.fetchParams.timeRange
        .getStartTime()
        .toDate()
        .valueOf(),
      currentEndTimestamp: this.props.fetchParams.timeRange
        .getEndTime()
        .toDate()
        .valueOf(),
      totalPages: Math.ceil(data.totalRows / this.state.devicesPerPage),
    });

    const element = this.scrollRef.current;
    if (element) {
      element.addEventListener("scroll", this.syncHorizontalWindow, {
        passive: true,
      });
      window.addEventListener("resize", this.syncHorizontalWindow);
      this.syncHorizontalWindow();
    }
  }

  componentWillUnmount(): void {
    this.scrollRef.current?.removeEventListener(
      "scroll",
      this.syncHorizontalWindow
    );
    window.removeEventListener("resize", this.syncHorizontalWindow);
    if (this.scrollFrame !== null) {
      window.cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = null;
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevProps?.data?.totalRows !== this.props.data.totalRows ||
      prevState?.data?.totalRows !== this.state?.data?.totalRows
    ) {
      const data = this.state.data || this.props.data;
      this.setState({
        totalPages: Math.ceil(data.totalRows / this.state.devicesPerPage),
        currentStartTimestamp: this.props.fetchParams.timeRange
          .getStartTime()
          .toDate()
          .valueOf(),
        currentEndTimestamp: this.props.fetchParams.timeRange
          .getEndTime()
          .toDate()
          .valueOf(),
      });
    }
  }

  render() {
    // The panel reads state data if it is present, else it reads props data.
    // We set the state data only when the user interacts with the panel, like changing the page number or changing the sort order.
    // When the user interacts with the panel, we freeze the timerange and do pagination inside that timerange. When he resets, then we again use global/relative timerange and show data from props.
    const data = this.state.data ?? this.props.data;
    const stateData = this.state.data;

    const sortOrder =
      (this.state.sortOrder || this.props.panelMeta.sortOrder) === "asc"
        ? "ascending"
        : "descending";

    const rawColumns = this.props.panelMeta.columns;
    const virtualizeColumns =
      (rawColumns?.filter((column) => column !== "timestamp").length ?? 0) >
      VIRTUALIZE_COLUMN_THRESHOLD;

    const layout = virtualizeColumns
      ? this.getColumnLayout(rawColumns, data?.data, this.getCellFont())
      : null;
    const columns =
      layout?.columns ??
      rawColumns?.filter((column) => column !== "timestamp") ??
      [];

    // scrollLeft is measured from the table's left edge, so the leading cells
    // come off it before it maps onto a column offset.
    const scrolledIntoColumns = layout
      ? Math.max(
          0,
          this.state.scrollLeft -
            layout.leadingWidths.reduce((sum, width) => sum + width, 0)
        )
      : 0;
    const viewportWidth = this.state.viewportWidth || window.innerWidth;

    const firstVisible = layout
      ? Math.max(
          0,
          findColumnAt(layout.offsets, scrolledIntoColumns) - COLUMN_OVERSCAN
        )
      : 0;
    const lastVisible = layout
      ? Math.min(
          columns.length,
          findColumnAt(layout.offsets, scrolledIntoColumns + viewportWidth) +
            1 +
            COLUMN_OVERSCAN
        )
      : columns.length;
    const renderedColumns = layout
      ? columns.slice(firstVisible, lastVisible)
      : columns;

    const leadingSpacerWidth = layout ? layout.offsets[firstVisible] : 0;
    const trailingSpacerWidth = layout
      ? layout.offsets[columns.length] - layout.offsets[lastVisible]
      : 0;
    const spacerStyle = (width: number): React.CSSProperties => ({
      width: `${width}px`,
      minWidth: `${width}px`,
      maxWidth: `${width}px`,
      padding: 0,
      border: "none",
    });
    const fixedWidth = (width: number): React.CSSProperties => ({
      width: `${width}px`,
      minWidth: `${width}px`,
      maxWidth: `${width}px`,
    });
    const columnStyleAt = (index: number): React.CSSProperties | undefined =>
      layout ? fixedWidth(layout.widths[firstVisible + index]) : undefined;
    const leadingColumnStyle = layout
      ? fixedWidth(layout.leadingWidths[0])
      : undefined;
    const timestampColumnStyle = layout
      ? fixedWidth(layout.leadingWidths[1])
      : undefined;

    if (data?.data && data.data.length > 0) {
      return (
        <TableContainer>
          <div className="tableContentContainer" ref={this.scrollRef}>
            <Dimmer.Dimmable dimmed={this.state.loading}>
              <Dimmer active={this.state.loading}>
                <LoadingAnimation
                  loadingText="Loading"
                  loaderSize="42px"
                  loaderColor="#FFFFFF"
                  textColor="#FFFFFF"
                />
              </Dimmer>

              <Table
                sortable={!this.state.loading}
                compact
                selectable
                unstackable
                size="small"
              >
                <Table.Header>
                  <Table.Row>
                    {data?.data[0]?.["-serial_metadata"] ? (
                      <TableHeaderCell
                        style={{ cursor: "unset", ...leadingColumnStyle }}
                        textAlign="center"
                      >
                        {`#${capitalizeFirstLetter(Object?.keys(data?.data[0]?.["-serial_metadata"]).toString())}`}
                      </TableHeaderCell>
                    ) : (
                      <TableHeaderCell
                        style={{ cursor: "unset", ...leadingColumnStyle }}
                        textAlign="center"
                      >
                        Device ID
                      </TableHeaderCell>
                    )}
                    <TableHeaderCell
                      sorted={sortOrder}
                      onClick={this.onChangeSortOrder.bind(this)}
                      textAlign="center"
                      style={timestampColumnStyle}
                    >
                      Timestamp
                    </TableHeaderCell>

                    {leadingSpacerWidth > 0 && (
                      <TableHeaderCell
                        aria-hidden
                        style={spacerStyle(leadingSpacerWidth)}
                      />
                    )}
                    {renderedColumns.map((column, index) => (
                      <TableHeaderCell
                        style={{ cursor: "unset", ...columnStyleAt(index) }}
                        key={column}
                        textAlign="center"
                      >
                        {column}
                      </TableHeaderCell>
                    ))}
                    {trailingSpacerWidth > 0 && (
                      <TableHeaderCell
                        aria-hidden
                        style={spacerStyle(trailingSpacerWidth)}
                      />
                    )}
                  </Table.Row>
                </Table.Header>

                <Table.Body>
                  {data.data.map((p, i) => {
                    return (
                      <Table.Row key={`${p.id}-${p.timestamp}-${i}`}>
                        {/* // If the "-serial_metadata" is present, then show the first key of the "-serial_metadata" as the device id. */}
                        {p?.["-serial_metadata"] ? (
                          <Table.Cell
                            textAlign="center"
                            style={leadingColumnStyle}
                          >
                            {
                              p?.["-serial_metadata"][
                                Object.keys(p?.["-serial_metadata"])[0]
                              ]
                            }
                          </Table.Cell>
                        ) : (
                          <Table.Cell
                            textAlign="center"
                            style={leadingColumnStyle}
                          >
                            {p.id}
                          </Table.Cell>
                        )}
                        <Table.Cell
                          textAlign="center"
                          style={timestampColumnStyle}
                        >
                          {formatUtcDateTime(p.timestamp)}
                        </Table.Cell>
                        {leadingSpacerWidth > 0 && (
                          <Table.Cell
                            aria-hidden
                            style={spacerStyle(leadingSpacerWidth)}
                          />
                        )}
                        {renderedColumns.map((column, index) => (
                          <Table.Cell
                            key={column}
                            textAlign="center"
                            style={columnStyleAt(index)}
                          >
                            {this.renderValue(column, p[column])}
                          </Table.Cell>
                        ))}
                        {trailingSpacerWidth > 0 && (
                          <Table.Cell
                            aria-hidden
                            style={spacerStyle(trailingSpacerWidth)}
                          />
                        )}
                      </Table.Row>
                    );
                  })}
                </Table.Body>
              </Table>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                {!this.state.loading && this.state.totalPages !== 0 && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: "16px",
                    }}
                  >
                    <StyledPagination
                      activePage={this.state.currentPage}
                      siblingRange={1}
                      totalPages={this.state.totalPages}
                      onPageChange={this.handlePaginationChange.bind(this)}
                    />

                    <StyledCardSearchPageInput
                      icon="search"
                      placeholder="Jump to page..."
                      name="activePage"
                      min={1}
                      onChange={this.handlePaginationInputChange}
                      onKeyDown={(event) =>
                        this.handlePaginationInputKeyDown(
                          event,
                          this.state.totalPages
                        )
                      }
                      type="number"
                      value={
                        this.state.inputPageNumber
                          ? this.state.inputPageNumber
                          : ""
                      }
                    />
                    <StyledSecondaryDevicePerPageWidget>
                      <MenuItem>Rows per page</MenuItem>
                      <MenuItem style={{ padding: "0px" }}>
                        <SelectDevicesPerPage
                          pointing="bottom"
                          compact
                          selection
                          options={rowsPerPageOptions}
                          value={this.state.devicesPerPage}
                          onChange={this.changeDevicesPerPage.bind(this)}
                        />
                      </MenuItem>
                    </StyledSecondaryDevicePerPageWidget>
                  </div>
                )}

                {stateData &&
                this.state.currentStartTimestamp &&
                this.state.currentEndTimestamp ? (
                  <TimeRangeContainer>
                    Timerange:{" "}
                    {formatUtcDateTime(this.state.currentStartTimestamp)} -{" "}
                    {formatUtcDateTime(this.state.currentEndTimestamp)}
                    <Popup
                      content="Timerange fixed due to panel interaction. Click to refresh"
                      position="top center"
                      inverted
                      trigger={
                        <Icon
                          name="redo"
                          color="blue"
                          style={{ marginLeft: "10px", cursor: "pointer" }}
                          onClick={this.onRefresh.bind(this)}
                        />
                      }
                    />
                  </TimeRangeContainer>
                ) : (
                  <></>
                )}
              </div>
            </Dimmer.Dimmable>
          </div>
        </TableContainer>
      );
    } else {
      return <div className="panel-no-data">No Data</div>;
    }
  }
}
